import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { InquiryCartProvider } from "@/context/InquiryCartContext";
import { SiteDataProvider } from "@/context/SiteDataContext";
import CodeInjection from "@/components/CodeInjection";
import SmoothScroll from "@/components/SmoothScroll";
import Layout from "@/components/layout/Layout";
// Public pages are bundled eagerly. Route-level lazy loading was measured and
// made things worse here: the pages share so much (framer-motion, the layout
// primitives, the shadcn components) that webpack duplicated the shared code
// across chunks, growing both the initial bundle and the total download.
import Home from "@/pages/Home";
import About from "@/pages/About";
import Partners from "@/pages/Partners";
import Catalogue from "@/pages/Catalogue";
import ProductDetail from "@/pages/ProductDetail";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Blog from "@/pages/Blog";
import BlogDetail from "@/pages/BlogDetail";
import Contact from "@/pages/Contact";
import InquiryCart from "@/pages/InquiryCart";
import Enquiry from "@/pages/Enquiry";
import Search from "@/pages/Search";
import { Privacy, Terms } from "@/pages/Legal";
import NotFound from "@/pages/NotFound";

/**
 * The CMS is code-split: a visitor browsing the catalogue never downloads the
 * admin bundle, the Firebase Auth/Storage modules or the editor components.
 */
const AdminApp = lazy(() => import("@/admin/AdminApp"));

const AdminFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#f6f8f9]">
    <div className="flex items-center gap-3 text-greyish">
      <span className="w-5 h-5 rounded-full border-2 border-aqua border-t-transparent animate-spin" />
      Loading admin…
    </div>
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <SiteDataProvider>
          <InquiryCartProvider>
            {/* Applies the head / body snippets configured in the CMS. */}
            <CodeInjection />
            <Routes>
              {/* Admin / CMS — outside the public Layout, its own chrome. */}
              <Route
                path="/admin/*"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <AdminApp />
                  </Suspense>
                }
              />

              {/* Public website */}
              <Route
                element={
                  <>
                    <SmoothScroll />
                    <Layout />
                  </>
                }
              >
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/products" element={<Catalogue />} />
                <Route path="/products/:slug" element={<ProductDetail />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/:slug" element={<ServiceDetail />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogDetail />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/cart" element={<InquiryCart />} />
                <Route path="/enquiry" element={<Enquiry />} />
                <Route path="/search" element={<Search />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
            <Toaster
              position="bottom-center"
              toastOptions={{ style: { borderRadius: "8px" } }}
              richColors
            />
          </InquiryCartProvider>
        </SiteDataProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
